import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import MainLayout from "../layouts/MainLayout";
import { formatDate, titleCase } from "../utils/format";

const STATUS_CONTENT = {
  pending_approval: {
    severity: "info",
    icon: <AccessTimeRoundedIcon fontSize="large" />,
    title: "Your business application is under review",
    description:
      "You can sign in and review your application. Store tools become available as soon as the workspace is approved.",
  },
  suspended: {
    severity: "warning",
    icon: <BlockRoundedIcon fontSize="large" />,
    title: "Workspace access is suspended",
    description:
      "Business operations and connector synchronization are currently blocked. Contact our team to resolve the account or payment issue.",
  },
  rejected: {
    severity: "error",
    icon: <BlockRoundedIcon fontSize="large" />,
    title: "Workspace application was not approved",
    description:
      "Review the platform note below and contact our team if you need clarification or want the application reviewed again.",
  },
  access_expired: {
    severity: "warning",
    icon: <AccessTimeRoundedIcon fontSize="large" />,
    title: "Your 30-day subscription expired",
    description:
      "Business features and connector synchronization are locked. Contact our team after payment so we can activate the next 30-day period.",
  },
};

function DetailCard({ label, value }) {
  return (
    <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary" fontWeight={850}>
          {label}
        </Typography>
        <Typography fontWeight={900} sx={{ mt: 0.55 }}>
          {value || "—"}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function OnboardingStatus() {
  const {
    selectedWorkspace,
    selectedStore,
    workspaceOperational,
    refreshWorkspaces,
    isSystemAccount,
  } = useAuth();

  if (isSystemAccount) return <Navigate to="/platform" replace />;
  if (workspaceOperational) return <Navigate to="/dashboard" replace />;

  const status =
    selectedWorkspace?.effective_status || selectedWorkspace?.status || "pending_approval";
  const content = STATUS_CONTENT[status] || STATUS_CONTENT.pending_approval;

  return (
    <MainLayout>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" fontWeight={950} letterSpacing={-0.7}>
            Workspace approval
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.7 }}>
            Track your workspace review and subscription access status here.
          </Typography>
        </Box>

        <Alert
          severity={content.severity}
          icon={content.icon}
          sx={{ borderRadius: 4, alignItems: "flex-start", py: 2 }}
          action={
            <Button
              color="inherit"
              startIcon={<RefreshRoundedIcon />}
              onClick={() => refreshWorkspaces()}
            >
              Refresh
            </Button>
          }
        >
          <Typography variant="h6" fontWeight={900}>
            {content.title}
          </Typography>
          <Typography sx={{ mt: 0.5 }}>{content.description}</Typography>
        </Alert>

        {selectedWorkspace?.status_reason && (
          <Alert severity="warning" sx={{ borderRadius: 3 }}>
            <Typography fontWeight={900}>Platform note</Typography>
            <Typography sx={{ mt: 0.4 }}>{selectedWorkspace.status_reason}</Typography>
          </Alert>
        )}

        <Card sx={{ borderRadius: 4, border: "1px solid #e2e8f0" }}>
          <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ md: "center" }}
              gap={2}
              mb={3}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 3,
                    bgcolor: "#eff6ff",
                    color: "#1d4ed8",
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <StorefrontRoundedIcon />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={950}>
                    {selectedWorkspace?.name || "Workspace"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedStore?.name || "Main Store"}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={titleCase(status)}
                  color={content.severity === "error" ? "error" : "warning"}
                  variant="outlined"
                  sx={{ fontWeight: 850 }}
                />
                <Chip
                  label={`Billing: ${titleCase(selectedWorkspace?.billing_status)}`}
                  variant="outlined"
                  sx={{ fontWeight: 850 }}
                />
              </Stack>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} lg={3}>
                <DetailCard
                  label="Requested onboarding"
                  value={titleCase(selectedWorkspace?.onboarding_method)}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <DetailCard
                  label="Business type"
                  value={selectedWorkspace?.business_type}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <DetailCard
                  label="Access expires"
                  value={formatDate(selectedWorkspace?.access_expires_at)}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <DetailCard
                  label="Days remaining"
                  value={selectedWorkspace?.effective_status === "active" ? `${selectedWorkspace?.days_remaining || 0} day(s)` : "Access locked"}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <DetailCard
                  label="Subscription started"
                  value={formatDate(selectedWorkspace?.subscription_started_at)}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <DetailCard
                  label="Last payment"
                  value={formatDate(selectedWorkspace?.last_payment_at)}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Alert severity="success" icon={<CheckCircleRoundedIcon />} sx={{ borderRadius: 3 }}>
          After each manual payment is recorded, the workspace is active for 30 days. A POS system is optional; manual products and inventory remain supported.
        </Alert>
      </Stack>
    </MainLayout>
  );
}
